import { Button, ButtonV2 } from '@cimplico/components';
import { useState } from 'react';
import { Account } from '../../types/account';
import { FieldValues, FormProvider, useForm } from 'react-hook-form';
import { useChartOfAccounts } from '../../queries/use-chart-of-accounts';
import { WorksheetStatusEnum } from '../../types/worksheet';
import { isAllNullOrUndefined, parseToCents } from '../new-workpaper/utils';
import TrialBalanceErrorAlert from '../new-workpaper/TrialBalanceErrorAlert';
import TrialBalanceUpload, { TrialBalanceUploadErrors } from '../new-workpaper/TrialBalanceUpload';
import { useWorkpaperContext } from '../../providers/WorkpaperProvider';
import { useReplaceManualTrialBalance } from '../../queries/use-trial-balance';
import { ManualTrialBalanceRow } from '../../types/workpaper';
import { useFirmContext } from '../../providers/FirmProvider';
import { useNavigate } from 'react-router-dom';
import { toastNotification } from '../../components/toast/Toast';

type UpdateTrialBalanceFormFields = {
    trialBalance?: ManualTrialBalanceRow[];
};

type UpdateTrialBalanceFormProps = {
    onClose: () => void;
};

function UpdateTrialBalanceForm({ onClose }: UpdateTrialBalanceFormProps): JSX.Element {
    const navigate = useNavigate();
    const [trialBalanceErrors, setTrialBalanceErrors] = useState<TrialBalanceUploadErrors>();
    const { id, entity } = useWorkpaperContext();
    const { shortId } = useFirmContext();

    const replaceTrialBalanceQuery = useReplaceManualTrialBalance();
    const methods = useForm<UpdateTrialBalanceFormFields>();

    function onUpload(data: FieldValues): void {
        if (trialBalanceErrors) {
            return;
        }

        if (!data.trialBalance) {
            methods.setError('trialBalance', { message: 'A trial balance is required.' });
        } else {
            handleUpdate();
        }
    }

    function handleUpdate(): void {
        const values = methods.getValues();
        const trialBalance = values.trialBalance ?? [];

        replaceTrialBalanceQuery.mutate(
            { workpaperId: id, trialBalance },
            {
                onError: (err) => {
                    const res = err.response?.data;
                    if (!res) {
                        toastNotification('error', 'Unable to update the trial balance!', 'An unknown error occurred');
                        return;
                    }
                    if ('message' in res) {
                        toastNotification('error', 'Unable to update the trial balance!', res.message);
                    } else {
                        toastNotification('error', 'Unable to update the trial balance!', res.errors.join('\n'));
                    }
                },
                onSuccess: (res) => {
                    toastNotification(
                        'success',
                        'Successfully updated the trial balance.',
                        `${res.createdCount} worksheets created, ${res.updatedCount} updated, ${res.excludedCount} excluded`
                    );
                    navigate(`/${shortId}/workpapers/${id}/trial-balance`);
                },
            }
        );
    }

    return (
        <div className="mx-auto w-full max-w-[450px] mt-8">
            <FormProvider {...methods}>
                {trialBalanceErrors && <TrialBalanceErrorAlert errors={trialBalanceErrors} />}
                <form className="flex flex-col gap-8" onSubmit={methods.handleSubmit(onUpload)}>
                    <TrialBalanceUpload onError={setTrialBalanceErrors} workpaperId={id} entity={entity} />
                    <div className="flex justify-end gap-4">
                        <ButtonV2 type="hollow" label="Cancel" onClick={onClose} size="sm" />
                        <ButtonV2
                            type="primary"
                            size="sm"
                            label="Save"
                            isSubmit
                            isLoading={replaceTrialBalanceQuery.isLoading}
                            isDisabled={replaceTrialBalanceQuery.isLoading}
                        />
                    </div>
                </form>
            </FormProvider>
        </div>
    );
}

export default UpdateTrialBalanceForm;
